import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup } from "@angular/forms";
import { Select, Store } from '@ngxs/store';
import { ActionsExecuting, actionsExecuting } from '@ngxs-labs/actions-executing';
import { Observable } from 'rxjs';
import { CustomIcons } from '@shared/models/custom-icons';
import { GetCatalogInfo, GetSelectCatalog } from '@shared/state/catalog/shared-catalog.action';
import { take, tap } from 'rxjs/operators';
import { GetUserDataProfiles, ShowLayout } from '@shared/state/shared/shared.action';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AuthState } from '@auth/store/auth/auth.state';
import { UserInfo } from '@auth/models/auth.state';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { CatalogView } from '@shared/models/shared-catalog.state';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { MapComponent } from '@shared/components/map/components/map/map.component';
import { ReleaseStatus } from '@shared/models/catalog.service';
import { Locale } from '@shared/models/locale';
import { TranslateService } from '@ngx-translate/core';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import { GetSelectCatalogItem, SetCurrentCatalogVersionRelease } from '../../../../../data/store/catalog/catalog.action';
import { CatalogItemsState } from '../../../../../data/modules/catalog-items-data/store/catalog-items-state';
import { ImpairedCatalogItemsRoutes } from '../../../../../version-for-visually-impaired/modules/data/modules/models/routes';
import { VersionForVisuallyImpairedRoutes } from '../../../../../version-for-visually-impaired/models/routes';
import { CatalogItemsDataRouting } from '../../../../../data/modules/catalog-items-data/models/catalog-items-data-routing';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'appeals-main-app',
  templateUrl: './appeals-main.component.html',
  styleUrls: ['./appeals-main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppealsMainComponent implements OnInit {
  @Select(actionsExecuting([GetSelectCatalog, GetCatalogInfo, GetUserDataProfiles])) public loadingData$: Observable<ActionsExecuting>;
  @Select(SharedCatalogState.selectCatalog) public selectCatalog$: Observable<CatalogView>;
  @ViewChild('topButton') public topButton: ElementRef;
  public user$: Observable<UserInfo | null>;
  public appealId: number | null;
  public title: string;
  public catalogItemId: number;
  public isImpaired = false;
  public step: 1 | 2 = 1;
  public langChange$: Observable<LangChangeEvent>;
  public form: FormGroup | any;
  public map: MapComponent;
  public icons = CustomIcons;
  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
    tap(res => {
      if (!this.isImpaired) {
        if (res.matches) {
          this.store.dispatch(new ShowLayout(false));
        } else {
          this.store.dispatch(new ShowLayout(true));
        }
      }
    })
  );
  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private store: Store,
    private breakpointObserver: BreakpointObserver,
    private snackBar: SnackBarService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    this.langChange$ = this.translateService.onLangChange.pipe(
      tap(res => {
        if ((res.lang as Locale) === Locale.EN_US) {
          this.goBack(true);
        }
      })
    );

    this.isImpaired = this.router.url.includes('impaired-appeals');
    const params = this.activeRoute.snapshot.queryParams;
    this.catalogItemId = params?.catalogItemGlobalId;
    const catalogId = params?.catalogGlobalId;
    if (!catalogId && isNaN(+catalogId)) {
      this.router.navigate(['opendata']);
      return;
    }

    this.store
      .dispatch([new GetSelectCatalog(+catalogId), new GetCatalogInfo(+catalogId)])
      .pipe(take(1))
      .subscribe(() => {
        const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
        if (selectCatalog) {
          this.store.dispatch(
            new SetCurrentCatalogVersionRelease(selectCatalog.id, {
              catalogId: selectCatalog.id,
              releaseNum: selectCatalog.actualReleaseNum,
              versionNum: selectCatalog.actualVersionNum,
              releaseDate: selectCatalog.actualRelease,
              versionDate: selectCatalog.actualVersion,
              releaseDateFull: '',
              defaultDate: '',
              releaseStatus: selectCatalog.actualRelease ? ReleaseStatus.Actual : ReleaseStatus.New,
              countRows: selectCatalog.countRows
            })
          );
        }

        if (
          !selectCatalog ||
          !this.store.selectSnapshot(SharedCatalogState.selectCatalogInfo) ||
          !this.store.selectSnapshot(SharedCatalogState.catalogInfo)
        ) {
          let queryParams: any = {};
          if (this.catalogItemId) {
            queryParams = { objectId: this.catalogItemId };
          }

          const currentCatalog = this.store.selectSnapshot(CatalogItemsState.getCatalogItem).call(this, +catalogId);

          if (currentCatalog && currentCatalog.isDynamic !== undefined) {
            queryParams = {
              isDynamic: currentCatalog.isDynamic
            };
          }

          if (currentCatalog && currentCatalog.pageSize && (currentCatalog.pageIndex || currentCatalog.pageIndex === 0)) {
            queryParams = {
              ...queryParams,
              pageSize: currentCatalog.pageSize,
              pageIndex: currentCatalog.pageIndex
            };
          }
          const backUrl = `/opendata/${selectCatalog.datasetId}`;

          this.router.navigate([backUrl], {
            queryParams: queryParams,
            skipLocationChange: true
          });
          this.snackBar.open(MirSnackbarType.Error, [
            {
              message: 'SNACK_BAR_MESSAGE.ERROR_CREATE_APPEAL_FORM',
              translate: true
            }
          ]);
        }
      });
    if (this.catalogItemId && !isNaN(this.catalogItemId)) {
      this.store.dispatch(new GetSelectCatalogItem(+this.catalogItemId, +catalogId, params?.isDynamic === 'true', false, true));
    }

    if (this.catalogItemId) {
      this.title = 'DATA.SHARED.APPEALS.REPORT_FALSE_DATA';
    } else {
      this.title = 'DATA.SHARED.APPEALS.FORM_ADDRESS_PROPOSITION_NEW_ITEM';
    }
    this.user$ = this.store.select(AuthState.authenticatedUser).pipe(
      tap(user => {
        if (user && user.token) {
          this.store.dispatch(new GetUserDataProfiles());
        }
      })
    );
  }

  public goStep1(): void {
    this.step = 1;
    this.topButton.nativeElement.scrollLeft = 0;
    if (this.appealId) {
      this.appealId = null;
    }
  }

  public goStep2(): void {
    if (this.form && !this.form?.invalid) {
      this.step = 2;
      this.topButton.nativeElement.scrollLeft = this.topButton.nativeElement.scrollWidth;
    }
  }

  public goBack(isEnLang = false): void {
    const params = this.activeRoute.snapshot.queryParams;
    const catalogItemGlobalId = params?.catalogItemGlobalId;
    const isBackHome = params?.isBackHome;
    const catalogGlobalId = params?.catalogGlobalId;

    const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    let backUrl = `opendata/${selectCatalog.datasetId}`;

    if (isEnLang) {
      backUrl += `/${CatalogItemsDataRouting.Passport}`;
    } else {
      backUrl += `/${CatalogItemsDataRouting.CatalogItems}`;
    }
    if (this.isImpaired) {
      backUrl = `visually-impaired/${VersionForVisuallyImpairedRoutes.DataSet}/${catalogGlobalId}/${ImpairedCatalogItemsRoutes.Table}`;
    }

    let queryParams: any = {};

    if (catalogItemGlobalId) {
      queryParams = { objectId: catalogItemGlobalId };
    }

    const currentCatalog = this.store.selectSnapshot(CatalogItemsState.getCatalogItem).call(this, +catalogGlobalId);
    if (isBackHome && isBackHome === 'true') {
      backUrl = '/';
      queryParams = {
        catalogItemGlobalId: catalogItemGlobalId,
        catalogGlobalId: catalogGlobalId
      };
    }

    if (currentCatalog && currentCatalog.isDynamic !== undefined) {
      queryParams = {
        isDynamic: currentCatalog.isDynamic
      };
    }

    if (currentCatalog && currentCatalog.pageSize && (currentCatalog.pageIndex || currentCatalog.pageIndex === 0)) {
      queryParams = {
        ...queryParams,
        pageSize: currentCatalog.pageSize,
        pageIndex: currentCatalog.pageIndex
      };
    }

    this.router.navigate([backUrl], {
      queryParams: queryParams
    });
  }

  public cancel(): void {
    this.goBack();
  }

  public formInit($event: FormGroup): void {
    this.form = $event;
    this.cdr.detectChanges();
  }
}
